import React, { useRef, useEffect, useState } from "react";
// import "./ChooseEase.css";
// import { FiArrowRight } from "react-icons/fi";
// import { Link } from "react-router-dom";

// Images
import jobWorkImg from "../../../images/jobwork.webp";
import materialPlanningImg from "../../../images/material.webp";
import productionImg from "../../../images/production.webp";
import inventoryImg from "../../../images/inventory.webp";
import purchaseImg from "../../../images/purchase.webp";
import salesImg from "../../../images/sales.webp";

interface Card {
  title: string;
  description: string;
  color: string;
  textColor: string;
  image: string;
  link?: string;
}

const cards: Card[] = [
  {
    title: "Sales Management",
    description:
      "Track leads, manage orders, and streamline your entire sales process from quote to invoice.",
    color: "#FFFFFF",
    textColor: "#333",
    image: salesImg,
    link: "/services/sales-management",
  },
  {
    title: "Purchase Management",
    description:
      "Easily manage suppliers, purchase orders, and incoming stock to avoid delays.",
    color: "#FFFFFF",
    textColor: "#333",
    image: purchaseImg,
    link: "/services/purchase-management",
  },
  {
    title: "Multi-Store Inventory",
    description:
      "Monitor stock across all locations in real-time with batch, barcode, and FIFO tracking.",
    color: "#FFFFFF",
    textColor: "#333",
    image: inventoryImg,
    link: "/services/inventory-management",
  },
  {
    title: "Production Management",
    description:
      "Plan, track, and control manufacturing with BOM, work orders, and quality checks.",
    color: "#FFFFFF",
    textColor: "#333",
    image: productionImg,
    link: "/services/production-management",
  },
  {
    title: "Material Planning",
    description:
      "Forecast material needs and ensure timely procurement to prevent stockouts.",
    color: "#FFFFFF",
    textColor: "#333",
    image: materialPlanningImg,
    link: "/services/material-planning",
  },
  {
    title: "Job Work Management",
    description:
      "Manage outsourced manufacturing with clear tracking of material flow and timelines.",
    color: "#FFFFFF",
    textColor: "#333",
    image: jobWorkImg,
    link: "/services/jobwork-management",
  },
];

const ChooseEase: React.FC = () => {
  const stackRef = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      if (isMobile) {
        stackRef.current.forEach((card, index) => {
          if (!card) return;
          card.style.transform = "none";
          card.style.opacity = "1";
          card.style.zIndex = String(cards.length + index);
        });
        return;
      }

      const scrollTop = window.pageYOffset;
      const containerTop = containerRef.current.offsetTop;
      const windowHeight = window.innerHeight;

      const stackingStartPoint = containerTop - windowHeight + 300;
      const relativeScroll = Math.max(0, scrollTop - stackingStartPoint);

      const cardHeight = 100;
      const stackingOffset = 20;
      const triggerInterval = 150;

      stackRef.current.forEach((card, index) => {
        if (!card) return;

        const triggerPoint = index * triggerInterval;

        if (relativeScroll >= triggerPoint) {
          const progress = (relativeScroll - triggerPoint) / triggerInterval;
          const clampedProgress = Math.min(progress, 1);

          const translateY =
            -clampedProgress * (cardHeight - stackingOffset * (index + 1));

          const scale = 0.95 + 0.05 * clampedProgress;
          const opacity = 0.7 + 0.3 * clampedProgress;

          card.style.transform = `translateY(${translateY}px) scale(${scale})`;
          card.style.opacity = String(opacity);
          card.style.zIndex = String(cards.length + index);
        } else {
          card.style.transform = `translateY(0px) scale(0.95)`;
          card.style.opacity = "0.7";
          card.style.zIndex = String(cards.length - index);
        }
      });
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [isMobile]);

  const getResponsiveStyles = () => {
    return {
      chooseEaseContainer: {
        padding: isMobile ? "15px" : "20px",
        backgroundColor: "#fff",
        fontFamily: "sans-serif",
        minHeight: isMobile ? "auto" : "200vh",
      } as React.CSSProperties,
      stackInfo: {
        textAlign: "center",
        maxWidth: isMobile ? "100%" : "900px",
        margin: isMobile ? "0 auto 30px" : "0 auto 100px",
        padding: isMobile ? "20px 0" : "10px 0",
      } as React.CSSProperties,
      title: {
        fontSize: isMobile ? "24px" : "27px",
        fontWeight: "700",
        marginBottom: "10px",
        color: "#1a1a1a",
      } as React.CSSProperties,
      description: {
        fontSize: "16px",
        color: "#576578",
        lineHeight: "1.6",
      } as React.CSSProperties,
      stackContainer: {
        position: "relative",
        height: isMobile ? "auto" : "1000px",
        marginTop: "-50px",
      } as React.CSSProperties,
      stackWrapper: {
        position: "relative",
        width: "100%",
        maxWidth: isMobile ? "100%" : "1000px",
        margin: "0 auto",
        display: isMobile ? "block" : "block",
      } as React.CSSProperties,
      stackCard: {
        position: "sticky" as const,
        margin: "0 auto",
        width: "100%",
        height: isMobile ? "450px" : "370px",
        maxWidth: isMobile ? "100%" : "1100px",
        borderRadius: isMobile ? "24px" : "44px",
        padding: isMobile ? "20px" : "30px",
        background: "white",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        transformOrigin: "center top",
        border: "1.5px solid rgb(0, 0, 0)",
        opacity: 1,
      } as React.CSSProperties,
      stackInnerCard: {
        display: "flex",
        flexDirection: isMobile ? ("column" as const) : ("row" as const),
        justifyContent: "space-between",
        alignItems: "center",
        gap: isMobile ? "20px" : "40px",
        height: '300px'
      } as React.CSSProperties,
      cardContent: {
        flex: "1",

        textAlign: isMobile ? "center" : "left",
      } as React.CSSProperties,
      cardTitle: {
        marginBottom: "10px",
        fontSize: isMobile ? "20px" : "28px",
        fontWeight: "600",
        lineHeight: "1.3",
      } as React.CSSProperties,
      cardDescription: {
        fontSize: isMobile ? "14px" : "16px",
        lineHeight: "1.6",
        color: "#4a5568",
      } as React.CSSProperties,
      cardImage: {
        width: isMobile ? "100%" : "500px",
        height: isMobile ? "120px" : "180px",
        maxWidth: isMobile ? "300px" : "550px",
        flexShrink: 0,
        borderRadius: "22px",
        border: "1px solid #e2e8f0",
        objectFit: "fill" as const,
        margin: isMobile ? "0 auto" : "0",
      } as React.CSSProperties,
      bottomSpacer: {
        height: isMobile ? "20px" : "1300px",
      } as React.CSSProperties,
      cardLink: {
        display: "inline-block",
        marginTop: "12px",
        fontSize: isMobile ? "14px" : "15px",
        fontWeight: "500",
        color: "black",
        textDecoration: "none",
        cursor: "pointer",
        transition: "color 0.2s ease",
      } as React.CSSProperties,
    };
  };

  const styles = getResponsiveStyles();

  return (
    <div style={styles.chooseEaseContainer}>
      <div style={styles.stackInfo}>
        <h3 style={styles.title}>Why Choose Ease Margin</h3>
        <p style={styles.description}>
          Ease Margin is built to help businesses optimize operations, reduce
          manual tasks, and drive profitability with seamless automation and
          real-time insights.
        </p>
      </div>

      <div style={styles.stackContainer} ref={containerRef}>
        <div style={styles.stackWrapper}>
          {cards.map((card, index) => (
            <div
              key={index}
              style={{
                ...styles.stackCard,
                backgroundColor: card.color,
                color: card.textColor,
                top: isMobile
                  ? `${84 + index * 25}px`
                  : `${120 + index * 25}px`,
                marginTop: isMobile ? (index === 0 ? 0 : 16) : 0,
                zIndex: cards.length + index,
              }}
              ref={(el) => (stackRef.current[index] = el)}
            >
              <div style={styles.stackInnerCard}>
                {/* <div style={styles.cardContent}>
                  <h3 style={styles.cardTitle}>{card.title}</h3>
                  <p style={styles.cardDescription}>{card.description}</p>
                  {card.link && (
                    <a
                      href={card.link}
                      // style={styles.cardLink}
                      className="cardLink"
                      onMouseOver={(e) =>
                        ((e.target as HTMLElement).style.color = "#0c5dc0")
                      }
                      onMouseOut={(e) =>
                        ((e.target as HTMLElement).style.color = "#1a73e8")
                      }
                    >
                      Know More →
                    </a>
                  )}
                </div> */}
                <div style={styles.cardContent}>
                  <h3 className="cardTitle">{card.title}</h3>
                  <p className="cardDescription">{card.description}</p>
                  {card.link && (
                    <a
                      href={card.link}
                      // className="cardLink"
                      style={styles.cardLink}
                    // onMouseOver={(e) => ((e.target as HTMLElement).style.color = "#0c5dc0")}
                    // onMouseOut={(e) => ((e.target as HTMLElement).style.color = "#1a73e8")}
                    >
                      Know More →
                    </a>
                  )}
                </div>

                <img
                  src={card.image}
                  alt={`${card.title} Illustration`}
                  // className="cardImage"
                  style={{ width: '400px', height: isMobile ? '250px' : '100%', borderRadius: '44px' }}
                // style={styles.cardImage}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={styles.bottomSpacer}></div>
    </div>
  );
};

export default ChooseEase;
